/* ==========================================================================
   Taana — product.js
   Variant picker · product info sync · media gallery + zoom · sticky ATC ·
   delivery estimate · pincode integration hook · share · recently viewed.
   ========================================================================== */
(() => {
  const Theme = (window.Theme = window.Theme || {});
  const money = (c) => Theme.utils.formatMoney(c);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variant-json]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.sectionId = this.dataset.sectionId;
      this.fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
      this.onChange = () => this.update(true);
      this.addEventListener('change', this.onChange);
      this.update(false);
    }
    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }
    get selected() {
      return this.fieldsets.map((fs) => {
        const checked = fs.querySelector('input:checked');
        const select = fs.querySelector('select');
        return checked ? checked.value : select ? select.value : null;
      });
    }
    findVariant(selected) {
      if (selected.some((v) => v === null)) return null;
      return this.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
    }
    /* Mark values that cannot be bought given the other current selections. */
    markAvailability(selected) {
      this.fieldsets.forEach((fs, i) => {
        fs.querySelectorAll('input[type="radio"]').forEach((input) => {
          // Variants that have this value AND match every other option the shopper has chosen.
          const matches = this.variants.filter((v) =>
            v.options.every((o, idx) => (idx === i ? o === input.value : selected[idx] === null || o === selected[idx]))
          );
          const exists = matches.length > 0;
          const available = matches.some((v) => v.available);
          const label = fs.querySelector(`label[for="${input.id}"]`);
          if (label) {
            label.classList.toggle('is-unavailable', !available);
            label.classList.toggle('is-nonexistent', !exists);
            const sr = label.querySelector('.variant-picker__sr-state');
            if (sr) sr.textContent = available ? '' : `— ${exists ? Theme.strings.soldOut : Theme.strings.unavailable}`;
          }
        });
        const value = fs.querySelector('[data-selected-value]');
        if (value) value.textContent = selected[i] || '';
      });
    }
    update(userInitiated) {
      const selected = this.selected;
      const variant = this.findVariant(selected);
      this.markAvailability(selected);
      const awaiting = selected.some((v) => v === null);
      this.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant, awaiting, sectionId: this.sectionId, userInitiated, picker: this },
      }));
      if (userInitiated && variant && this.hasAttribute('data-update-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({ path: url.toString() }, '', url.toString());
      }
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Product info: reacts to variant:change within its scope ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.onVariant = (e) => this.apply(e.detail);
      this.addEventListener('variant:change', this.onVariant);
      // Sticky bar lives outside this element; forward changes to it.
      this.sectionId = this.dataset.sectionId;
      this.initDelivery();
      this.initPincode();
      this.initShare();
      this.trackRecentlyViewed();
    }
    disconnectedCallback() {
      this.removeEventListener('variant:change', this.onVariant);
      this.pincodeForm && this.pincodeForm.removeEventListener('submit', this.onPincode);
      this.shareBtn && this.shareBtn.removeEventListener('click', this.onShare);
    }
    apply({ variant, awaiting }) {
      const scope = this;
      const forms = document.querySelectorAll(`#ProductForm-${this.sectionId}, [data-sticky-form="${this.sectionId}"]`);
      const available = !!(variant && variant.available);
      let label = Theme.strings.addToCart;
      if (awaiting) label = scope.dataset.selectLabel || Theme.strings.addToCart;
      else if (!variant) label = Theme.strings.unavailable;
      else if (!variant.available) label = Theme.strings.soldOut;

      forms.forEach((form) => {
        const input = form.querySelector('[data-variant-id]');
        if (input) input.value = variant ? variant.id : '';
        form.querySelectorAll('[data-add-button]').forEach((btn) => {
          btn.disabled = !available;
          const lbl = btn.querySelector('[data-add-label]');
          if (lbl) lbl.textContent = label;
        });
        const dyn = form.querySelector('[data-dynamic-checkout]');
        if (dyn) dyn.classList.toggle('is-disabled', !available);
      });

      // Price
      const html = variant ? this.priceHtml(variant) : null;
      document.querySelectorAll(`[data-product-price="${this.sectionId}"]`).forEach((el) => {
        if (html) el.innerHTML = html;
      });

      // Stock
      const stock = this.querySelector('[data-stock]');
      if (stock) {
        let msg = '';
        let tone = '';
        if (variant && variant.available && variant.low_stock) { msg = Theme.strings.lowStock.replace('[count]', variant.low_stock); tone = 'low'; }
        else if (variant && variant.available && !variant.backorder) { msg = Theme.strings.inStock; tone = 'ok'; }
        stock.textContent = msg;
        stock.dataset.tone = tone;
        stock.hidden = !msg;
      }

      // Back in stock: only for an exact, sold-out variant
      this.querySelectorAll('[data-back-in-stock]').forEach((el) => {
        el.hidden = !(variant && !variant.available);
        const field = el.querySelector('[data-bis-variant]');
        if (field && variant) field.value = `${variant.title} (${variant.id})`;
        el.dataset.variantId = variant ? variant.id : '';
      });

      // SKU
      const sku = this.querySelector('[data-sku]');
      if (sku) {
        sku.textContent = variant && variant.sku ? variant.sku : '';
        sku.parentElement.hidden = !(variant && variant.sku);
      }

      // Media
      if (variant && variant.media_id) {
        const gallery = document.querySelector(`media-gallery[data-section-id="${this.sectionId}"]`);
        if (gallery && gallery.show) gallery.show(variant.media_id);
      }

      document.dispatchEvent(new CustomEvent('taana:variant:change', { detail: { variant, sectionId: this.sectionId } }));
    }
    priceHtml(v) {
      const sale = v.compare_at_price > v.price;
      let out = '';
      if (sale) {
        const pct = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
        out = `<span class="price__sale">${money(v.price)}</span><s class="price__compare">${money(v.compare_at_price)}</s>`;
        if (this.dataset.showSave !== undefined) out += `<span class="price__save">−${pct}%</span>`;
      } else {
        out = `<span class="price__regular">${money(v.price)}</span>`;
      }
      if (v.unit_price) out += `<span class="t-xs t-muted unit-price">${v.unit_price}</span>`;
      return out;
    }
    initDelivery() {
      const el = this.querySelector('[data-delivery]');
      if (!el) return;
      const min = Number(el.dataset.min) || 0;
      const max = Number(el.dataset.max) || 0;
      if (!min || !max) return;
      const add = (days) => {
        const d = new Date();
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          if (d.getDay() !== 0) added += 1; // skip Sundays
        }
        return d;
      };
      const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { weekday: 'short', day: 'numeric', month: 'short' });
      const target = el.querySelector('[data-delivery-range]');
      if (target) target.textContent = `${fmt.format(add(min))} – ${fmt.format(add(max))}`;
    }
    /* Pincode: dispatches an event for a serviceability app to handle.
       Without an app we never claim serviceability — we show the general estimate. */
    initPincode() {
      this.pincodeForm = this.querySelector('[data-pincode-form]');
      if (!this.pincodeForm) return;
      this.onPincode = (e) => {
        e.preventDefault();
        const input = this.pincodeForm.querySelector('input');
        const out = this.pincodeForm.parentElement.querySelector('[data-pincode-result]');
        const pincode = (input.value || '').trim();
        if (!/^\d{6}$/.test(pincode)) {
          input.setAttribute('aria-invalid', 'true');
          if (out) { out.hidden = false; out.textContent = this.pincodeForm.dataset.invalid; }
          return;
        }
        input.removeAttribute('aria-invalid');
        const evt = new CustomEvent('taana:pincode:check', { detail: { pincode, result: out }, cancelable: true, bubbles: true });
        const unhandled = this.dispatchEvent(evt);
        if (unhandled && out) {
          out.hidden = false;
          out.textContent = this.pincodeForm.dataset.fallback;
        }
      };
      this.pincodeForm.addEventListener('submit', this.onPincode);
    }
    initShare() {
      this.shareBtn = this.querySelector('[data-share]');
      if (!this.shareBtn) return;
      this.onShare = async () => {
        const url = this.shareBtn.dataset.url || window.location.href;
        if (navigator.share) {
          try { await navigator.share({ title: document.title, url }); } catch (e) { /* dismissed */ }
        } else if (navigator.clipboard) {
          await navigator.clipboard.writeText(url);
          Theme.toast(this.shareBtn.dataset.copied);
        }
      };
      this.shareBtn.addEventListener('click', this.onShare);
    }
    trackRecentlyViewed() {
      const handle = this.dataset.productHandle;
      if (!handle || this.hasAttribute('data-no-track')) return;
      try {
        const key = 'taana:recently-viewed';
        const list = (JSON.parse(localStorage.getItem(key)) || []).filter((h) => h !== handle);
        list.unshift(handle);
        localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
      } catch (e) { /* storage unavailable */ }
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /* Back-in-stock: give restock apps the first chance to handle the request. */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest && e.target.closest('[data-bis-form]');
    if (!form) return;
    const wrap = form.closest('[data-back-in-stock]');
    const info = form.closest('product-info');
    const evt = new CustomEvent('taana:back-in-stock', {
      bubbles: true,
      cancelable: true,
      detail: {
        email: form.elements['contact[email]'] ? form.elements['contact[email]'].value : '',
        variantId: wrap ? wrap.dataset.variantId : '',
        productHandle: info ? info.dataset.productHandle : '',
        form,
      },
    });
    if (!form.dispatchEvent(evt)) e.preventDefault(); // an app handled it
  });

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.items = Array.from(this.querySelectorAll('[data-media-id]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.counter = this.querySelector('[data-gallery-counter]');
      this.onThumb = (e) => {
        const t = e.target.closest('[data-thumb]');
        if (!t) return;
        e.preventDefault();
        this.show(t.dataset.thumb, true);
      };
      this.onScroll = () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.syncFromScroll());
      };
      this.onZoom = (e) => {
        const z = e.target.closest('[data-zoom-index]');
        if (!z) return;
        e.preventDefault();
        const modal = document.getElementById(`ZoomModal-${this.dataset.sectionId}`);
        if (modal && modal.openAt) modal.openAt(Number(z.dataset.zoomIndex), z);
      };
      this.addEventListener('click', this.onThumb);
      this.addEventListener('click', this.onZoom);
      this.track && this.track.addEventListener('scroll', this.onScroll, { passive: true });
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onThumb);
      this.removeEventListener('click', this.onZoom);
      this.track && this.track.removeEventListener('scroll', this.onScroll);
      cancelAnimationFrame(this.raf);
    }
    isSlider() {
      return this.track && this.track.scrollWidth > this.track.clientWidth + 4;
    }
    show(mediaId, fromThumb) {
      const item = this.items.find((i) => i.dataset.mediaId === String(mediaId));
      if (!item) return;
      this.setActive(item);
      const behavior = Theme.utils.prefersReducedMotion() ? 'auto' : 'smooth';
      if (this.isSlider()) {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior });
      } else if (this.dataset.layout === 'stack' || this.dataset.layout === 'grid') {
        if (fromThumb || this.dataset.scrollOnVariant !== undefined) {
          const top = item.getBoundingClientRect().top + window.scrollY - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0) - 16;
          window.scrollTo({ top, behavior });
        }
      } else {
        // Thumbnail layout: swap the featured item to the front.
        this.items.forEach((i) => i.classList.toggle('is-featured', i === item));
      }
    }
    setActive(item) {
      this.items.forEach((i) => i.classList.toggle('is-active', i === item));
      const id = item.dataset.mediaId;
      this.thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.thumb === id)));
      if (this.counter) this.counter.textContent = `${this.items.indexOf(item) + 1} / ${this.items.length}`;
      // Pause any other playing videos
      this.items.forEach((i) => {
        if (i !== item) i.querySelectorAll('video').forEach((v) => v.pause());
      });
    }
    syncFromScroll() {
      if (!this.isSlider()) return;
      const x = this.track.scrollLeft;
      let closest = this.items[0];
      let min = Infinity;
      this.items.forEach((i) => {
        const d = Math.abs(i.offsetLeft - this.track.offsetLeft - x);
        if (d < min) { min = d; closest = i; }
      });
      if (closest && !closest.classList.contains('is-active')) this.setActive(closest);
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* ---------- Zoom modal (full-screen, swipeable, native pinch) ---------- */
  class ZoomModal extends Theme.OverlayPanel {
    openAt(index, opener) {
      this.open(opener);
      const track = this.querySelector('[data-zoom-track]');
      const slides = track ? track.children : [];
      const target = slides[index];
      if (target) {
        target.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
        requestAnimationFrame(() => track.scrollTo({ left: target.offsetLeft, behavior: 'auto' }));
      }
    }
  }
  if (!customElements.get('zoom-modal')) customElements.define('zoom-modal', ZoomModal);

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.main = document.querySelector(`#ProductForm-${this.sectionId} [data-add-button]`);
      this.footer = document.querySelector('.site-footer');
      if (!this.main) return;
      this.mainVisible = true;
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.main) this.mainVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
          if (entry.target === this.footer) this.footerVisible = entry.isIntersecting;
        });
        this.classList.toggle('is-visible', !this.mainVisible && !this.footerVisible);
        this.setAttribute('aria-hidden', String(this.mainVisible || !!this.footerVisible));
      });
      this.io.observe(this.main);
      if (this.footer) this.io.observe(this.footer);
      this.onClick = (e) => {
        const btn = e.target.closest('[data-sticky-choose]');
        if (!btn) return;
        e.preventDefault();
        const picker = document.querySelector(`variant-picker[data-section-id="${this.sectionId}"]`);
        if (picker) {
          const top = picker.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top, behavior: Theme.utils.prefersReducedMotion() ? 'auto' : 'smooth' });
          const firstEmpty = Array.from(picker.querySelectorAll('fieldset')).find((fs) => !fs.querySelector('input:checked'));
          const focusTarget = (firstEmpty || picker).querySelector('input');
          setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 500);
        }
      };
      this.addEventListener('click', this.onClick);
      this.onVariant = (e) => {
        if (e.detail.sectionId !== this.sectionId) return;
        const v = e.detail.variant;
        const choose = this.querySelector('[data-sticky-choose]');
        const add = this.querySelector('[data-add-button]');
        const needsChoice = !v;
        if (choose) choose.hidden = !needsChoice;
        if (add) add.hidden = needsChoice;
      };
      document.addEventListener('taana:variant:change', this.onVariant);
    }
    disconnectedCallback() {
      this.io && this.io.disconnect();
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('taana:variant:change', this.onVariant);
    }
  }
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

  /* ---------- Frequently bought together ---------- */
  class BundlePicker extends HTMLElement {
    connectedCallback() {
      this.onChange = (e) => {
        const select = e.target.closest && e.target.closest('select[data-bundle-variant]');
        if (select) {
          const opt = select.selectedOptions[0];
          const input = select.closest('[data-bundle-card]').querySelector('input[data-bundle-item]');
          if (input && opt) { input.value = opt.value; input.dataset.price = opt.dataset.price; }
        }
        this.recalc();
      };
      this.onSubmit = async (e) => {
        e.preventDefault();
        const items = Array.from(this.querySelectorAll('input[data-bundle-item]:checked')).map((i) => ({ id: Number(i.value), quantity: 1 }));
        if (!items.length) return;
        const btn = this.querySelector('[type="submit"]');
        btn && btn.classList.add('is-loading');
        try { await Theme.cart.add(items, { source: 'bundle' }); } catch (err) { Theme.toast(err.message, { tone: 'error' }); }
        finally { btn && btn.classList.remove('is-loading'); }
      };
      this.form = this.querySelector('form');
      this.addEventListener('change', this.onChange);
      this.form && this.form.addEventListener('submit', this.onSubmit);
      this.recalc();
    }
    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.form && this.form.removeEventListener('submit', this.onSubmit);
    }
    recalc() {
      const checked = Array.from(this.querySelectorAll('input[data-bundle-item]:checked'));
      const total = checked.reduce((sum, i) => sum + Number(i.dataset.price || 0), 0);
      const out = this.querySelector('[data-bundle-total]');
      if (out) out.textContent = money(total);
      const btn = this.querySelector('[type="submit"]');
      if (btn) btn.disabled = checked.length === 0;
      this.querySelectorAll('[data-bundle-card]').forEach((card) => {
        const input = card.querySelector('input[data-bundle-item]');
        card.classList.toggle('is-excluded', input && !input.checked);
      });
    }
  }
  if (!customElements.get('bundle-picker')) customElements.define('bundle-picker', BundlePicker);

  /* ---------- Product recommendations (related / complementary) ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.io.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 400px 0px' });
      this.io.observe(this);
    }
    disconnectedCallback() {
      this.io && this.io.disconnect();
    }
    async load() {
      this.loaded = true;
      try {
        const res = await fetch(this.dataset.url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.querySelector('[data-rec-content]')) {
          this.innerHTML = fresh.innerHTML;
          this.classList.add('is-loaded');
          const host = this.closest('[data-rec-host]');
          if (host) host.classList.add('is-loaded');
          const section = this.closest('.shopify-section');
          if (section && Theme.motion) Theme.motion.init(section);
          document.dispatchEvent(new CustomEvent('taana:layout-change'));
        } else {
          const host = this.closest('[data-rec-host]') || this;
          host.hidden = true;
          host.style.display = 'none';
        }
      } catch (e) {
        /* optional content */
      }
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
})();
